import { GameId, ProductV2 } from '@common'
import { HeroModuleProps, HeroModuleTarget } from '@common/models/storeContent'
import cx from 'classnames'
import Image, { StaticImageData } from 'next/image'

import { useGameImages } from '@/components/games/images'
import { HeroProduct } from '@/features/products/HeroProduct'

import { config } from '../../../config'

import styles from './GameHero.module.scss'

type Props = {
  gameSlug: GameId
  heroContents: HeroModuleProps | null
  mobileHeroProduct?: ProductV2
}

function resolveHeroPosition(heroContents: HeroModuleProps | null, target: HeroModuleTarget) {
  const x = heroContents?.[target]?.imagePosition?.x
  const y = heroContents?.[target]?.imagePosition?.y
  if (x !== undefined && y !== undefined) {
    return { objectPosition: `${x}% ${y}%` }
  }

  return undefined
}

function resolveHeroSrc(heroContents: HeroModuleProps | null, target: HeroModuleTarget, fallback: StaticImageData) {
  const src = heroContents?.[target]?.src
  return src ? `${config.APP_URL}/${src}` : fallback
}

export const GameHero = ({ gameSlug, heroContents, mobileHeroProduct }: Props) => {
  const { gameImage } = useGameImages(gameSlug)
  const isSquad = gameSlug === 'squadbusters'

  return (
    <>
      <div className={cx(styles.Hero, styles.Desktop)}>
        <Image
          src={resolveHeroSrc(heroContents, 'desktop', gameImage('heroDesktop'))}
          style={resolveHeroPosition(heroContents, 'desktop')}
          objectFit="cover"
          alt="hero"
          layout="fill"
          className={styles.Image}
          priority
        />
        {heroContents?.desktop?.src ? (
          <div className={styles.HeroDesktopGameLogo}>
            <Image src={gameImage('logo')} alt="hero" objectFit="cover" layout="fill" />
          </div>
        ) : null}
      </div>
      <div className={styles.HeroMobileContainer}>
        <div className={cx(styles.Hero, styles.Mobile)}>
          <div className={styles.HeroMobileBackground}>
            <Image
              src={resolveHeroSrc(heroContents, 'mobile', gameImage('heroMobile'))}
              style={resolveHeroPosition(heroContents, 'mobile')}
              objectFit="cover"
              alt="hero"
              layout="fill"
              priority
            />
          </div>
          <div className={cx(styles.HeroMobileGameLogo, { [styles.squad]: isSquad })}>
            <Image src={gameImage('logo')} alt="hero" objectFit="contain" layout="fill" />
          </div>
          {mobileHeroProduct ? (
            <div className={styles.HeroMobileProductContainer}>
              <HeroProduct product={mobileHeroProduct} />
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}
